import { useEffect, useMemo } from 'react';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import Spinner from 'components/ui/Spinner';
import Table, { TableRow } from 'components/ui/Table';
import { Dialog } from 'components/ui/Modal/Modal';
import ErrorDisplay from 'components/Error/ErrorDisplay';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import { orderSelectors, orderThunks, clearCreditNoteOrder } from 'store/reducers/orderSlice';
import { faExclamationTriangle, faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled } from 'styled-components';
import { formatPrice } from 'utils/formatPrice';

type CreditNoteModalProps = {
    open: boolean;
    onClose: () => void;
    orderNumber: string | null;
    isDevMock?: boolean;
};

const CreditNoteModal = ({ open, onClose, orderNumber, isDevMock = false }: CreditNoteModalProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const creditNoteOrder = useAppSelector(orderSelectors.selectCreditNoteOrder);
    const loading = useAppSelector(orderSelectors.selectCreditNoteOrderLoading);
    const error = useAppSelector(orderSelectors.selectCreditNoteOrderError);

    // Fetch order data when modal opens
    useEffect(() => {
        if (open && orderNumber) {
            dispatch(orderThunks.getCreditNoteOrder(orderNumber));
        }
    }, [open, orderNumber, dispatch]);

    // Clear order data when modal closes
    const handleClose = () => {
        dispatch(clearCreditNoteOrder());
        onClose();
    };

    // Calculate total sum
    const totalSum = useMemo(() => {
        if (!creditNoteOrder?.orderLines) return null;
        const sum = creditNoteOrder.orderLines.reduce((acc, line) => acc + (line.totalPrice ?? 0), 0);
        const currency = creditNoteOrder.orderLines[0]?.currency;
        return { sum, currency };
    }, [creditNoteOrder]);

    // Build table rows from order lines
    const tableRows: TableRow[] = useMemo(() => {
        if (!creditNoteOrder?.orderLines) return [];

        return creditNoteOrder.orderLines.map((line, index) => ({
            key: `order-line-${index}`,
            cells: [
                {
                    el: (
                        <Text fontSize={13} thin color="textLight">
                            {line.sku ?? '-'}
                        </Text>
                    ),
                },
                {
                    el: (
                        <Flex column gap="0.125rem">
                            <Text fontSize={13}>{line.articleName ?? '-'}</Text>
                            {line.orderLineText && (
                                <Text fontSize={12} thin italic color="textLight">
                                    {line.orderLineText}
                                </Text>
                            )}
                        </Flex>
                    ),
                },
                {
                    el: <Text fontSize={13}>{line.quantityShipped ?? 0}</Text>,
                    align: 'center' as const,
                },
                {
                    el: (
                        <Text fontSize={13} thin>
                            {formatPrice(line.price, line.currency)}
                        </Text>
                    ),
                    align: 'right' as const,
                },
                {
                    el: (
                        <Text fontSize={13} bold>
                            {formatPrice(line.totalPrice, line.currency)}
                        </Text>
                    ),
                    align: 'right' as const,
                },
            ],
        }));
    }, [creditNoteOrder]);

    const tableHeaders = [t('SKU'), t('productInformation'), t('quantity'), t('price'), t('totalPrice')];

    if (!open) return null;

    return (
        <Dialog
            header={`${t('creditNoteSummary')}: ${orderNumber ?? ''}`}
            onClose={handleClose}
            tint
            blur
            width="750px"
            maxHeight="80vh"
        >
            <ModalContent column gap="1.25rem">
                {isDevMock && (
                    <DevMockWarning className="mt-3" align="center" gap="0.5rem">
                        <FontAwesomeIcon icon={faExclamationCircle} />
                        <Text fontSize={12}>{t('creditNoteDevMockWarning')}</Text>
                    </DevMockWarning>
                )}
                {loading ? (
                    <LoadingWrapper $grow={1} align="center" justify="center">
                        <Spinner size="small" color="text" />
                    </LoadingWrapper>
                ) : error ? (
                    <ErrorDisplay errorMessage={error} title={t('error_occurred')} icon={faExclamationTriangle} />
                ) : (
                    <>
                        {/* Order Info Header */}
                        {creditNoteOrder && (
                            <Flex gap="1rem" $wrap className="pr-2 pl-2" justify="between">
                                {creditNoteOrder.customerCode && (
                                    <Flex column gap="0.20rem">
                                        <InfoLabel>{t('customerCode')}</InfoLabel>
                                        <InfoValue>{creditNoteOrder.customerCode}</InfoValue>
                                    </Flex>
                                )}
                                {creditNoteOrder.customerOrderNumber && (
                                    <Flex column gap="0.20rem">
                                        <InfoLabel>{t('orderReference')}</InfoLabel>
                                        <InfoValue>{creditNoteOrder.customerOrderNumber}</InfoValue>
                                    </Flex>
                                )}
                                {creditNoteOrder.orderDateTime && (
                                    <Flex column gap="0.20rem">
                                        <InfoLabel>{t('orderDateTime')}</InfoLabel>
                                        <InfoValue>{creditNoteOrder.orderDateTime.str}</InfoValue>
                                    </Flex>
                                )}
                                {creditNoteOrder.orderType && (
                                    <Flex column gap="0.20rem">
                                        <InfoLabel>{t('orderType')}</InfoLabel>
                                        <InfoValue>{creditNoteOrder.orderType}</InfoValue>
                                    </Flex>
                                )}

                                {creditNoteOrder.statusCode && (
                                    <Flex column gap="0.20rem">
                                        <InfoLabel>{t('status')}</InfoLabel>
                                        <InfoValue>{creditNoteOrder.statusCode}</InfoValue>
                                    </Flex>
                                )}
                            </Flex>
                        )}

                        {/* Order Lines Table */}
                        {creditNoteOrder?.orderLines && creditNoteOrder.orderLines.length > 0 ? (
                            <TableSection column>
                                <TableWrapper>
                                    <Table
                                        className="credit-note-table"
                                        bgColor="fillMedium"
                                        cols={tableHeaders.length}
                                        headers={tableHeaders}
                                        rows={tableRows}
                                        tintOddRows
                                    />
                                </TableWrapper>
                                {totalSum && (
                                    <TotalRow justify="end" align="center">
                                        <Text fontSize={14} bold style={{ marginRight: '1rem' }}>
                                            {t('totalPrice')}:
                                        </Text>
                                        <TotalAmount>{formatPrice(totalSum.sum, totalSum.currency)}</TotalAmount>
                                    </TotalRow>
                                )}
                            </TableSection>
                        ) : (
                            <EmptyState align="center" justify="center" $grow={1}>
                                <Text fontSize={14} thin color="textLight">
                                    {t('noOrderLinesFound')}
                                </Text>
                            </EmptyState>
                        )}
                    </>
                )}
            </ModalContent>
        </Dialog>
    );
};

export default CreditNoteModal;

const DevMockWarning = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.warning}20;
    border: 1px solid ${({ theme }) => theme.colors.warning};
    padding: 0.75rem 1rem;
    border-radius: var(--radius);
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: 0.5rem;

    svg {
        color: ${({ theme }) => theme.colors.warning};
        flex-shrink: 0;
    }
`;

const ModalContent = styled(Flex)`
    padding: 1.25rem;
    padding-top: 3.5rem; /* Leave space for Dialog header */
    min-height: 200px;
`;

const LoadingWrapper = styled(Flex)`
    min-height: 150px;
`;

const InfoLabel = styled.span`
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.textLight};
`;

const InfoValue = styled.span`
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text};
`;

const TableSection = styled(Flex)`
    width: 100%;
`;

const TableWrapper = styled.div`
    width: 100%;
    overflow: auto;
    border-radius: var(--radius);
    border: 1px solid ${({ theme }) => theme.colors.fillMedium};
`;

const TotalRow = styled(Flex)`
    margin-top: 1rem;
    padding: 0.75rem 1rem;
    background-color: ${({ theme }) => theme.colors.fillLight};
    border-radius: var(--radius);
`;

const TotalAmount = styled.span`
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text};
`;

const EmptyState = styled(Flex)`
    min-height: 100px;
    padding: 2rem;
`;
